/**
 * Agent Surface Registry
 *
 * Runtime registry for agent surfaces, CLIs, and integrations.
 * Mirrors the database `agent_surfaces` table for TypeScript-side validation.
 *
 * Design goals:
 *   1. Agent-agnostic — new surfaces register without code changes
 *   2. Backward-compatible — legacy surfaces remain valid ids in persisted data
 *   3. Configurable — callers can supply a custom registry or use the global default
 *
 * Usage:
 *   import { agentSurfaceRegistry, AgentSurfaceRegistry } from './agent-registry';
 *
 *   // Inference (replaces hardcoded string matching)
 *   const source = agentSurfaceRegistry.inferSource('my-openclaw-daemon'); // 'openclaw'
 *
 *   // Registration (extend at runtime)
 *   agentSurfaceRegistry.register({
 *     id: 'new_agent',
 *     name: 'New Agent',
 *     category: 'memory_source',
 *     inferFromAgentName: ['newagent', 'na'],
 *   });
 *
 * @module agent-registry
 */

export type AgentSurfaceCategory =
  | 'memory_source'
  | 'event_agent'
  | 'cli_tool'
  | 'integration';

export interface AgentSurface {
  /** Canonical id — matches agent_surfaces.id in Postgres */
  id: string;
  /** Human-readable name */
  name: string;
  description?: string;
  category: AgentSurfaceCategory;
  /** Substrings to match against agent names for auto-inference */
  inferFromAgentName?: string[];
  /** Extensible metadata (version, vendor, capabilities, etc.) */
  metadata?: Record<string, unknown>;
}

/**
 * In-memory registry of agent surfaces.
 * Thread-safe for reads; mutations (register) should happen at bootstrap time.
 */
export class AgentSurfaceRegistry {
  private surfaces = new Map<string, AgentSurface>();

  register(surface: AgentSurface): void {
    this.surfaces.set(surface.id, surface);
  }

  get(id: string): AgentSurface | undefined {
    return this.surfaces.get(id);
  }

  has(id: string): boolean {
    return this.surfaces.has(id);
  }

  all(): AgentSurface[] {
    return Array.from(this.surfaces.values());
  }

  byCategory(category: AgentSurfaceCategory): AgentSurface[] {
    return this.all().filter((s) => s.category === category);
  }

  /**
   * Infer the memory source from an agent name.
   * Tests each registered surface's `inferFromAgentName` patterns against
   * the lower-cased agent name. Returns the first match, or 'extracted'.
   *
   * Replaces the hardcoded logic:
   *   if (name.includes('openclaw') || name.includes('claw')) return 'openclaw';
   */
  inferSource(agentName: string): string {
    const lower = agentName.toLowerCase();
    for (const surface of this.surfaces.values()) {
      const patterns = surface.inferFromAgentName ?? [];
      if (patterns.some((p) => lower.includes(p))) {
        return surface.id;
      }
    }
    return 'extracted';
  }

  /**
   * Build the set of valid source ids for a given category.
   */
  validSources(category?: AgentSurfaceCategory): string[] {
    const list = category ? this.byCategory(category) : this.all();
    return list.map((s) => s.id);
  }
}

// ─── Well-known constants ─────────────────────────────────────────────────
// Export these so callers can use them for type-safe references without
// hardcoding strings throughout the codebase.

export const AgentSurfaces = {
  CORTEX: 'cortex',
  GITHUB_COPILOT: 'github_copilot',
  BOX: 'box',
  OPCODE: 'opencode',
  HERMES: 'hermes',
  COPILOT_CLI: 'copilot_cli',
  OPENCLAW: 'openclaw',
  GEMINI_CLI: 'gemini-cli',
  USER: 'user',
  SYSTEM: 'system',
  AGENT: 'agent',
  INTEGRATION: 'integration',
  EXPLICIT: 'explicit',
  EXTRACTED: 'extracted',
} as const;

// ─── Default registry seed ────────────────────────────────────────────────

export const DEFAULT_AGENT_SURFACES: AgentSurface[] = [
  {
    id: AgentSurfaces.CORTEX,
    name: 'Cortex',
    description: 'In-app AI copilot and orchestrator',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.GITHUB_COPILOT,
    name: 'GitHub Copilot',
    description: 'GitHub Copilot CLI integration',
    category: 'memory_source',
    inferFromAgentName: ['copilot'],
  },
  {
    id: AgentSurfaces.BOX,
    name: 'Upstash Box',
    description: 'Per-principal box runtime',
    category: 'memory_source',
    inferFromAgentName: ['box'],
  },
  {
    id: AgentSurfaces.OPCODE,
    name: 'OpenCode',
    description: 'OpenCode headless agent server',
    category: 'memory_source',
    inferFromAgentName: ['opencode'],
  },
  {
    id: AgentSurfaces.HERMES,
    name: 'Hermes Agent',
    description: 'Self-improving Postgres-backed learning loop',
    category: 'memory_source',
    inferFromAgentName: ['hermes'],
  },
  {
    id: AgentSurfaces.COPILOT_CLI,
    name: 'Copilot CLI',
    description: 'GitHub Copilot CLI surface',
    category: 'memory_source',
    inferFromAgentName: ['copilot-cli'],
  },
  {
    id: AgentSurfaces.OPENCLAW,
    name: 'OpenClaw',
    description: 'OpenClaw skill registry',
    category: 'memory_source',
    inferFromAgentName: ['openclaw', 'claw'],
  },
  {
    id: AgentSurfaces.GEMINI_CLI,
    name: 'Gemini CLI',
    description: 'Google Gemini CLI',
    category: 'memory_source',
    inferFromAgentName: ['gemini'],
  },
  {
    id: AgentSurfaces.USER,
    name: 'User',
    description: 'Human user input',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.SYSTEM,
    name: 'System',
    description: 'System-generated',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.AGENT,
    name: 'Generic Agent',
    description: 'Unclassified agent',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.INTEGRATION,
    name: 'Integration',
    description: 'Third-party integration',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.EXPLICIT,
    name: 'Explicit',
    description: 'User explicitly requested memory',
    category: 'memory_source',
  },
  {
    id: AgentSurfaces.EXTRACTED,
    name: 'Extracted',
    description: 'AI-extracted from conversation',
    category: 'memory_source',
  },
];

/**
 * Create a pre-seeded registry with all default agent surfaces.
 */
export function createDefaultRegistry(): AgentSurfaceRegistry {
  const registry = new AgentSurfaceRegistry();
  for (const surface of DEFAULT_AGENT_SURFACES) {
    registry.register(surface);
  }
  return registry;
}

/**
 * Global singleton registry.
 * Safe to import and use from any module. Can be extended at app startup
 * via `agentSurfaceRegistry.register(...)`.
 */
export const agentSurfaceRegistry = createDefaultRegistry();

// ─── CLI Manifest Registry ────────────────────────────────────────────────
// Parallel registry for Box CLI tools so we don't hardcode CLI install logic.

export type CLICategory = 'core' | 'opt-in';

export interface AgentCLI {
  /** Canonical id — used in BoxImageConfig.installCLIs map keys */
  id: string;
  name: string;
  category: CLICategory;
  /** One or more bash commands to install the CLI (idempotent guards expected) */
  installCommands: string[];
  /** Optional commands to start a persistent service inside the box */
  bootstrapCommands?: string[];
  /** If the CLI exposes a gateway/port, the port number */
  port?: number;
  /** Environment variable name the CLI reads from config.json */
  envVar?: string;
  /** Extra metadata for the box orchestration connector */
  metadata?: Record<string, unknown>;
}

export class AgentCLIRegistry {
  private clis = new Map<string, AgentCLI>();

  register(cli: AgentCLI): void {
    this.clis.set(cli.id, cli);
  }

  get(id: string): AgentCLI | undefined {
    return this.clis.get(id);
  }

  has(id: string): boolean {
    return this.clis.has(id);
  }

  all(): AgentCLI[] {
    return Array.from(this.clis.values());
  }

  byCategory(category: CLICategory): AgentCLI[] {
    return this.all().filter((c) => c.category === category);
  }

  /** Render bash install blocks for the enabled CLI ids */
  renderInstallBlocks(enabled: Set<string>, context: {
    geminiApiKey?: string;
    githubToken?: string;
  }): string[] {
    const blocks: string[] = [];
    for (const id of enabled) {
      const cli = this.clis.get(id);
      if (!cli) continue;
      blocks.push(`# ${cli.name}${cli.category === 'opt-in' ? ' (opt-in)' : ''}`);
      for (const cmd of cli.installCommands) {
        blocks.push(cmd);
      }
      if (cli.envVar && cli.envVar === 'GEMINI_API_KEY' && context.geminiApiKey) {
        blocks.push('export GEMINI_API_KEY="$(jq -r .geminiApiKey /root/.flowstate/config.json)"');
      }
      if (cli.bootstrapCommands) {
        for (const cmd of cli.bootstrapCommands) {
          blocks.push(cmd);
        }
      }
    }
    return blocks;
  }
}

export const DEFAULT_AGENT_CLIS: AgentCLI[] = [
  {
    id: 'gemini',
    name: 'Gemini CLI',
    category: 'core',
    installCommands: ['(command -v gemini >/dev/null 2>&1) || npm install -g @google/gemini-cli'],
    envVar: 'GEMINI_API_KEY',
  },
  {
    id: 'opencode',
    name: 'OpenCode',
    category: 'core',
    installCommands: ['(command -v opencode >/dev/null 2>&1) || npm install -g opencode-ai'],
    bootstrapCommands: [
      '(pgrep -f "opencode serve" >/dev/null 2>&1) || nohup opencode serve --port 4096 > /var/log/opencode.log 2>&1 &',
    ],
    port: 4096,
  },
  {
    id: 'copilot',
    name: 'GitHub Copilot CLI',
    category: 'opt-in',
    installCommands: [
      '(command -v copilot >/dev/null 2>&1) || npm install -g @github/copilot-cli || echo "[warn] copilot-cli install failed"',
    ],
  },
  {
    id: 'openclaw',
    name: 'OpenClaw',
    category: 'opt-in',
    installCommands: [
      '(command -v openclaw >/dev/null 2>&1) || (npm install -g openclaw@latest && openclaw onboard --install-daemon || echo "[warn] openclaw install failed")',
    ],
    bootstrapCommands: [
      '(pgrep -f "openclaw gateway" >/dev/null 2>&1) || nohup openclaw gateway --port 18789 > /var/log/openclaw-gateway.log 2>&1 &',
    ],
    port: 18789,
  },
  {
    id: 'hermes',
    name: 'Hermes Agent',
    category: 'opt-in',
    installCommands: [
      '(command -v hermes >/dev/null 2>&1) || pipx install hermes-agent || echo "[warn] hermes-agent install failed"',
    ],
  },
];

export function createDefaultCLIRegistry(): AgentCLIRegistry {
  const registry = new AgentCLIRegistry();
  for (const cli of DEFAULT_AGENT_CLIS) {
    registry.register(cli);
  }
  return registry;
}

export const agentCLIRegistry = createDefaultCLIRegistry();
